import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { date, dateTime } from '../lib/format';
import { Card, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast } from '../components/ui';
import { STAGE_LABEL, STAGE_TONE, type CampaignRow, type ChannelRow, type PurposeRow } from './Campaigns';

/**
 * One retro data campaign: its trail, its numbers, and the people behind them.
 */

interface Stats {
  byDay: Array<{ day: string; sent: number; opened: number; responded: number }>;
  byPurpose: Array<{ purposeId: string; name: string; granted: number; declined: number }>;
  contacts: { total: number; failed: number };
}
interface Recipient {
  id: string; principalHash: string; identifierType: string | null; status: string; contactCount: number;
  sentAt: string | null; openedAt: string | null; respondedAt: string | null; expiresAt: string | null; lastError: string | null;
}
interface ReminderPreview { eligible: number; earliestAt: string | null; reason: string | null; nextNumber: number }

const STEPS: Array<{ key: string; label: string; stages: CampaignRow['stage'][] }> = [
  { key: 'draft', label: 'Draft', stages: ['draft'] },
  { key: 'audience', label: 'Audience locked', stages: ['audience_locked'] },
  { key: 'sending', label: 'Sending', stages: ['sending', 'paused'] },
  { key: 'waiting', label: 'Awaiting responses', stages: ['waiting', 'reminder'] },
  { key: 'closed', label: 'Closed', stages: ['closed', 'cancelled'] },
];

const RECIPIENT_STATUS: Record<string, string> = {
  pending: 'To send', sent: 'Sent', opened: 'Opened', responded: 'Responded', expired: 'Link expired',
  failed: 'Failed', suppressed: 'No contact detail',
};

const CLOSURE_ACTION: Record<string, string> = {
  keep_s7: 'Keep processing under a stated S.7 ground',
  stop_and_erase: 'Stop processing and mark for erasure',
  leave_open: 'Leave open for a later campaign',
};

export default function CampaignDetail() {
  const { id = '' } = useParams();
  const toast = useToast();
  const writable = can(getUser(), 'campaigns:edit');
  const sends = can(getUser(), 'campaigns:send');
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);

  const loaded = useResource<{ campaign: CampaignRow; audience: { total: number; contactable: number } }>(
    () => api.get(`/v1/campaigns/${id}`), [id, reload],
  );
  const stats = useResource<Stats>(() => api.get(`/v1/campaigns/${id}/stats`), [id, reload]);
  const reminder = useResource<ReminderPreview>(() => api.get(`/v1/campaigns/${id}/reminder`), [id, reload]);
  const recipients = useResource<{ recipients: Recipient[]; total: number }>(
    () => api.get(`/v1/campaigns/${id}/recipients?limit=50&offset=${page * 50}${filter ? `&status=${filter}` : ''}`),
    [id, reload, filter, page],
  );
  const purposes = useResource<{ purposes: PurposeRow[] }>(() => api.get('/v1/purposes'), []);
  const channels = useResource<{ channels: ChannelRow[] }>(() => api.get('/v1/channels'), []);

  async function act(label: string, path: string, body?: unknown, done?: string) {
    setBusy(label);
    try {
      await api.post(`/v1/campaigns/${id}/${path}`, body ?? {});
      toast(done ?? 'Done.', 'ok');
      setReload((n) => n + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  if (loaded.loading && !loaded.data) return <Loading what="Loading the campaign" />;
  if (loaded.error) return <ErrorNote error={loaded.error} />;
  if (!loaded.data) return null;

  const c = loaded.data.campaign;
  const k = c.counts;
  const stepIndex = STEPS.findIndex((s) => s.stages.includes(c.stage));
  const channel = channels.data?.channels.find((x) => x.id === c.channelId) ?? null;
  const purposeName = (pid: string) => purposes.data?.purposes.find((p) => p.id === pid)?.current.name ?? pid;
  const unanswered = (k.sent ?? 0) + (k.opened ?? 0) + (k.expired ?? 0);
  const reached = k.reached ?? 0;

  return (
    <>
      <PageHead eyebrow="Retro data campaigns" backTo="/campaigns" title={c.name}>
        <Pill tone={STAGE_TONE[c.stage]}>{STAGE_LABEL[c.stage]}</Pill>
        <Link className="btn" to="/campaigns">All campaigns</Link>
        {writable && c.stage === 'draft' && (
          <button onClick={() => act('lock', 'lock-audience', undefined, 'Audience locked.')} disabled={!!busy}>Lock audience</button>
        )}
        {sends && (c.stage === 'draft' || c.stage === 'audience_locked') && (
          <button className="primary" onClick={() => act('start', 'start', undefined, 'Sending started.')} disabled={!!busy}>Start sending</button>
        )}
        {sends && c.stage === 'paused' && (
          <button className="primary" onClick={() => act('resume', 'start', undefined, 'Sending resumed.')} disabled={!!busy}>Resume</button>
        )}
        {sends && (c.stage === 'sending' || c.stage === 'reminder') && (
          <>
            <button onClick={() => act('batch', 'send-batch', { max: 100 }, 'A batch went out.')} disabled={!!busy}>Send a batch now</button>
            <button onClick={() => act('pause', 'status', { status: 'paused' }, 'Paused.')} disabled={!!busy}>Pause</button>
          </>
        )}
        {sends && c.stage === 'waiting' && (
          <>
            <button
              onClick={() => act('remind', 'remind', undefined, 'Reminder wave started.')}
              disabled={!!busy || !reminder.data?.eligible}
              title={reminder.data?.reason ?? undefined}
            >
              Send reminder{reminder.data?.eligible ? ` to ${reminder.data.eligible}` : ''}
            </button>
            <button className="primary" onClick={() => setClosing(true)} disabled={!!busy}>Close campaign</button>
          </>
        )}
      </PageHead>

      <div className="stepper">
        {STEPS.map((s, i) => (
          <div key={s.key} className={`step ${i < stepIndex ? 'done' : ''} ${i === stepIndex ? 'now' : ''}`}>
            <span className="step-dot" aria-hidden />
            <span className="step-label">{s.label}</span>
            <span className="small muted step-when">
              {s.key === 'draft' && date(c.createdAt)}
              {s.key === 'audience' && date(c.audienceLockedAt)}
              {s.key === 'sending' && date(c.startedAt)}
              {s.key === 'waiting' && date(c.finishedAt)}
              {s.key === 'closed' && date(c.closedAt)}
            </span>
          </div>
        ))}
      </div>

      {c.stage === 'draft' && (
        <div className="note" style={{ marginBottom: 14 }}>
          Audience: <strong>{loaded.data.audience.contactable}</strong> contactable{loaded.data.audience.total !== loaded.data.audience.contactable ? `, ${loaded.data.audience.total - loaded.data.audience.contactable} without ${c.channel === 'email' ? 'an email address' : 'a mobile number'}` : ''}.
        </div>
      )}
      {c.stage === 'waiting' && reminder.data && (
        <div className={`note ${reminder.data.eligible ? 'warn' : ''}`} style={{ marginBottom: 14 }}>
          {reminder.data.eligible
            ? `Reminder ${reminder.data.nextNumber}: ${reminder.data.eligible} due.`
            : reminder.data.reason}
        </div>
      )}
      {c.stage === 'closed' && (
        <Card title="Closed">
          <div><strong>{CLOSURE_ACTION[c.closureAction ?? ''] ?? c.closureAction}</strong> for the {unanswered} people who did not respond.</div>
          <div className="small" style={{ marginTop: 6 }}>{c.closureDecision}</div>
          <div className="small muted" style={{ marginTop: 6 }}>Recorded {dateTime(c.closedAt)} by {c.closedBy}.</div>
        </Card>
      )}

      <div className="stat-row">
        <Card><div className="stat"><h3>Audience</h3><div className="value">{k.total || loaded.data.audience.contactable}</div>{k.suppressed ? <div className="sub">{k.suppressed} without a contact detail</div> : null}</div></Card>
        <Card><div className="stat"><h3>Reached</h3><div className="value">{reached}</div>{(k.failed || k.pending) ? <div className="sub">{k.failed ? `${k.failed} failed` : ''}{k.failed && k.pending ? ' · ' : ''}{k.pending ? `${k.pending} to send` : ''}</div> : null}</div></Card>
        <Card><div className="stat"><h3>Opened</h3><div className="value">{k.everOpened ?? 0}</div><div className="sub">{reached ? `${Math.round(((k.everOpened ?? 0) / reached) * 1000) / 10}% of reached` : ''}</div></div></Card>
        <Card><div className="stat"><h3>Responded</h3><div className={`value ${c.responseRate !== null && c.responseRate >= 30 ? 'ok' : ''}`}>{k.responded ?? 0}</div><div className="sub">{c.responseRate === null ? '' : `${c.responseRate}% of reached`}</div></div></Card>
      </div>

      <div className="grid cols-2" style={{ alignItems: 'start' }}>
        <div>
          <Card title="Activity by day">
            {stats.data && stats.data.byDay.length > 0 ? <DayChart rows={stats.data.byDay} /> : <div className="small muted">Nothing sent yet.</div>}
          </Card>

          <Card title="Decisions by purpose">
            {stats.data && stats.data.byPurpose.length > 0 ? (
              <table className="data">
                <thead><tr><th>Purpose</th><th className="num">Granted</th><th className="num">Declined</th></tr></thead>
                <tbody>
                  {stats.data.byPurpose.map((p) => (
                    <tr key={p.purposeId}><td>{p.name}</td><td className="num">{p.granted}</td><td className="num">{p.declined}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="small muted">No responses yet.</div>}
          </Card>

          <Card title="Waves">
            {c.waves.length === 0 && <div className="small muted">Not started.</div>}
            {c.waves.length > 0 && (
              <table className="data">
                <thead><tr><th>Wave</th><th>Started</th><th className="num">People</th><th className="num">Sent</th><th className="num">Failed</th><th /></tr></thead>
                <tbody>
                  {c.waves.map((w) => (
                    <tr key={w.id}>
                      <td>{w.kind === 'first' ? 'First send' : `Reminder ${w.number - 1}`} <span className="small muted">by {w.channel === 'sms' ? 'SMS' : w.channel === 'whatsapp' ? 'WhatsApp' : 'email'}</span></td>
                      <td className="small">{dateTime(w.startedAt)}</td>
                      <td className="num">{w.recipients}</td>
                      <td className="num">{w.sent}</td>
                      <td className="num">{w.failed}</td>
                      <td>{w.finishedAt ? <Pill tone="ok">Finished</Pill> : <Pill tone="brand">In progress</Pill>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        <div>
          <Card title="Setup">
            <table className="data">
              <tbody>
                <tr><td className="small muted" style={{ width: 140 }}>Purposes</td><td>{c.purposeIds.map(purposeName).join(', ')}</td></tr>
                <tr><td className="small muted">Language</td><td>{c.lang}</td></tr>
                <tr><td className="small muted">Sent by</td><td>{c.channel === 'sms' ? 'SMS' : c.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}{channel ? ` through ${channel.name} (${channel.summary})` : ' through the default channel'}</td></tr>
                <tr><td className="small muted">Who</td><td>{c.audience.mode === 'import' ? 'People brought in from an import' : c.audience.mode === 'decided_before' ? `Decided before ${date(c.audience.before)}` : c.audience.mode === 'all_contactable' ? 'Everyone contactable' : 'People with no decision on record'}{c.audience.limit ? `, at most ${c.audience.limit}` : ''}</td></tr>
                <tr><td className="small muted">Link valid for</td><td>{Math.round(c.linkTtlHours / 24)} days, single use</td></tr>
                <tr><td className="small muted">Reminders</td><td>At most {c.reminderRule.maxReminders}, earliest {c.reminderRule.earliestDays} days after the last contact</td></tr>
                {channel?.kind === 'api_pull' && (
                  <tr><td className="small muted">Sending</td><td className="small">Your system pulls the people to contact, each with a fresh link, from <code className="mono">GET /v1/campaigns/{c.id}/audience-export</code> with a service key that can read consent.</td></tr>
                )}
              </tbody>
            </table>
            {c.subject && <div className="small" style={{ marginTop: 10 }}><strong>{c.subject}</strong></div>}
            <pre className="small" style={{ whiteSpace: 'pre-wrap', marginTop: 6, fontFamily: 'inherit' }}>{c.body}</pre>
          </Card>

          <Card title={`Recipients${recipients.data ? ` · ${recipients.data.total}` : ''}`}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }} style={{ width: 'auto' }}>
                <option value="">Everyone</option>
                {Object.entries(RECIPIENT_STATUS).map(([v, l]) => <option key={v} value={v}>{l} ({k[v] ?? 0})</option>)}
              </select>
            </div>
            {recipients.data && (
              <>
                <table className="data">
                  <thead><tr><th>Record</th><th>State</th><th className="num">Contacts</th><th>Sent</th><th>Opened</th><th>Responded</th></tr></thead>
                  <tbody>
                    {recipients.data.recipients.map((r) => (
                      <tr key={r.id}>
                        <td className="mono small">{r.principalHash.slice(0, 10)}…</td>
                        <td>
                          <Pill tone={r.status === 'responded' ? 'ok' : r.status === 'failed' ? 'bad' : r.status === 'expired' ? 'warn' : undefined}>{RECIPIENT_STATUS[r.status] ?? r.status}</Pill>
                          {r.lastError && <div className="small muted">{r.lastError}</div>}
                        </td>
                        <td className="num">{r.contactCount}</td>
                        <td className="small muted">{date(r.sentAt)}</td>
                        <td className="small muted">{date(r.openedAt)}</td>
                        <td className="small muted">{date(r.respondedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {recipients.data.total > 50 && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                    <button className="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</button>
                    <span className="small muted">{page * 50 + 1} to {Math.min((page + 1) * 50, recipients.data.total)} of {recipients.data.total}</span>
                    <button className="small" disabled={(page + 1) * 50 >= recipients.data.total} onClick={() => setPage((p) => p + 1)}>Next</button>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </div>

      {closing && (
        <CloseModal
          unanswered={unanswered}
          busy={busy === 'close'}
          onClose={() => setClosing(false)}
          onConfirm={async (decision, action) => {
            await act('close', 'close', { decision, action }, 'Campaign closed.');
            setClosing(false);
          }}
        />
      )}
    </>
  );
}

function CloseModal({ unanswered, busy, onClose, onConfirm }: {
  unanswered: number; busy: boolean; onClose: () => void;
  onConfirm: (decision: string, action: 'keep_s7' | 'stop_and_erase' | 'leave_open') => void;
}) {
  const [action, setAction] = useState<'keep_s7' | 'stop_and_erase' | 'leave_open'>('keep_s7');
  const [decision, setDecision] = useState('');
  return (
    <Modal
      title="Close campaign"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" onClick={() => onConfirm(decision, action)} disabled={busy || !decision.trim()}>{busy ? 'Closing…' : 'Close campaign'}</button>
        </>
      }
    >
      <Field label={`Non-responders · ${unanswered}`}>
        <select value={action} onChange={(e) => setAction(e.target.value as typeof action)}>
          {Object.entries(CLOSURE_ACTION).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>
      <Field label="Decision" help="Purposes stopped or continued, ground, effective date.">
        <textarea rows={5} value={decision} onChange={(e) => setDecision(e.target.value)} />
      </Field>
    </Modal>
  );
}

/** Sent, opened and responded per day, as plain bars. Every bar is a count of rows. */
function DayChart({ rows }: { rows: Stats['byDay'] }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...rows.map((r) => Math.max(r.sent, r.opened, r.responded)));
  const width = 560; const height = 160; const pad = 24;
  const step = (width - pad * 2) / Math.max(1, rows.length);
  const y = (v: number) => height - pad - (v / max) * (height - pad * 2);
  const bar = (i: number, v: number, offset: number, fill: string) => (
    <rect key={`${i}-${offset}`} x={pad + i * step + offset * (step / 3.4)} y={y(v)} width={Math.max(1, step / 3.6)} height={height - pad - y(v)} fill={fill} />
  );
  const h = hover === null ? null : rows[hover];
  return (
    <div className="chart-wrap" onMouseLeave={() => setHover(null)}>
      {h && (
        <div className="chart-tip" style={{ left: `${((pad + hover! * step + step / 2) / width) * 100}%` }}>
          <strong>{h.day}</strong><br />
          Sent {h.sent} · Opened {h.opened} · Responded {h.responded}
        </div>
      )}
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Sent, opened and responded by day">
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="var(--line)" />
        <text x={pad} y={pad - 8} className="small" fill="var(--ink-3)" fontSize="10">{max}</text>
        {hover !== null && <rect x={pad + hover * step - 2} y={pad - 4} width={step} height={height - pad * 2 + 4} fill="var(--accent)" opacity="0.08" />}
        {rows.map((r, i) => [bar(i, r.sent, 0, 'var(--ink-3)'), bar(i, r.opened, 1, 'var(--accent)'), bar(i, r.responded, 2, 'var(--ok)')])}
        {rows.length <= 40 && rows.map((r, i) => (i % Math.ceil(rows.length / 8) === 0
          ? <text key={r.day} x={pad + i * step} y={height - 8} fontSize="9" fill="var(--ink-3)">{r.day.slice(5)}</text>
          : null))}
        {rows.map((r, i) => (
          <rect key={`hit-${r.day}`} x={pad + i * step - 2} y={0} width={step} height={height} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      <div className="small muted" style={{ display: 'flex', gap: 14 }}>
        <span><span className="swatch" style={{ background: 'var(--ink-3)' }} /> Sent</span>
        <span><span className="swatch" style={{ background: 'var(--accent)' }} /> Opened</span>
        <span><span className="swatch" style={{ background: 'var(--ok)' }} /> Responded</span>
      </div>
    </div>
  );
}
